import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles, Building2, Rocket, Crown, ArrowRight, ShoppingCart, Clock3, Mail, Loader2, X, CheckCircle2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getPlans, notifyPurchaseInterest } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const SALES_EMAIL = 'info@tecnologieverona.it';

const PLAN_META = {
  free: {
    icon: Sparkles, tone: 'slate',
    tag: 'Per iniziare',
    features: [
      'Push testo, file e URL',
      'Scadenza fino a 90 giorni',
      'Passphrase opzionale',
      'Dashboard sessione browser',
      'Verifica destinatari via email OTP',
    ],
    cta: 'Usa gratis',
  },
  starter: {
    icon: Rocket, tone: 'blue',
    tag: 'Più popolare',
    features: [
      'Tutto del Free',
      'Logo e colori personalizzati',
      'URL brandizzato /b/tuo-nome',
      'Testi hero personalizzabili',
      'Rimuove branding piattaforma',
    ],
    cta: 'Richiedi attivazione',
  },
  pro: {
    icon: Crown, tone: 'indigo',
    tag: 'Per il tuo studio o azienda',
    features: [
      'Tutto dello Starter',
      'Dominio custom (secure.tuobrand.it)',
      'SSL Let\'s Encrypt automatico',
      'Priorità supporto tecnico',
      'SLA 99.9%',
      'White-label completo',
    ],
    cta: 'Richiedi attivazione',
  },
};

export default function Pricing() {
  const [plans, setPlans] = useState(null);
  const { user, brand } = useAuth();
  const [purchaseModal, setPurchaseModal] = useState(null);
  const [notifying, setNotifying] = useState(false);
  const [notifySent, setNotifySent] = useState(false);
  const [notifyError, setNotifyError] = useState(null);

  useEffect(() => { getPlans().then(setPlans).catch(() => setPlans({})); }, []);

  const openPurchaseModal = (planId, planInfo) => {
    setPurchaseModal({ planId, planInfo });
    setNotifySent(false);
    setNotifyError(null);
  };
  const closePurchaseModal = () => {
    if (notifying) return;
    setPurchaseModal(null);
    setNotifySent(false);
    setNotifyError(null);
  };
  const sendPurchaseInterest = async () => {
    if (!purchaseModal) return;
    setNotifying(true);
    setNotifyError(null);
    try {
      const res = await notifyPurchaseInterest(purchaseModal.planId);
      if (res.ok) {
        setNotifySent(true);
        toast.success('Richiesta inviata', { description: `Ti contatteremo a breve all'indirizzo ${user?.email || ''}.` });
      } else {
        setNotifyError(res.error || 'Impossibile inviare la notifica. Riprova o scrivi a info@tecnologieverona.it');
      }
    } catch (e) {
      setNotifyError(e?.response?.data?.detail || 'Errore di rete. Scrivi direttamente a info@tecnologieverona.it');
    } finally {
      setNotifying(false);
    }
  };

  return (
    <div>
      <section className="tv-gradient-soft py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0d63b0]">Piani abbonamento</div>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold text-slate-900">Un piano per ogni esigenza</h1>
          <p className="mt-4 text-slate-600">Usa gratis oppure diventa reseller con il tuo brand e i tuoi clienti.</p>
          <div className="mt-4 inline-flex items-start gap-2 rounded-lg bg-amber-50 text-amber-900 border border-amber-200 px-4 py-2 text-sm">
            <Clock3 className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
            <span><strong>Acquisto online in fase di sviluppo.</strong> Clicca “Richiedi attivazione” per essere contattato e finalizzare il piano.</span>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {!plans && <div className="text-center text-slate-500">Caricamento piani...</div>}
          {plans && (
            <div className="grid md:grid-cols-3 gap-6">
              {Object.entries(plans).map(([id, p]) => {
                const meta = PLAN_META[id];
                const isCurrent = brand?.plan === id;
                const isFeatured = id === 'starter';
                const Ic = meta.icon;
                return (
                  <Card key={id} data-testid={`pricing-card-${id}`}
                    className={`relative border p-7 flex flex-col ${isFeatured ? 'border-[#0d63b0] shadow-xl ring-2 ring-[#0d63b0]/10' : 'border-slate-200'}`}>
                    {isFeatured && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0d63b0] text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow">{meta.tag}</div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                        <Ic className="h-5 w-5 text-[#0d63b0]" />
                      </div>
                      <div>
                        <div className="font-bold text-slate-900">{p.name}</div>
                        {!isFeatured && <div className="text-xs text-slate-500">{meta.tag}</div>}
                      </div>
                    </div>
                    <div className="mt-5 flex items-baseline">
                      <span className="text-4xl font-extrabold text-slate-900">€{p.price_eur}</span>
                      <span className="ml-1 text-sm text-slate-500">/ mese</span>
                    </div>
                    <ul className="mt-5 space-y-2.5 text-sm text-slate-700 flex-1">
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> <span><strong>File fino a {p.max_file_mb}MB</strong></span>
                      </li>
                      {meta.features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    {isCurrent ? (
                      <Button disabled className="mt-6 w-full bg-slate-200 text-slate-700 hover:bg-slate-200">Piano attivo</Button>
                    ) : id === 'free' ? (
                      <Button asChild variant="outline" className="mt-6 w-full" data-testid={`pricing-cta-${id}`}>
                        <Link to="/p/new">{meta.cta}</Link>
                      </Button>
                    ) : user ? (
                      <Button
                        data-testid={`pricing-cta-${id}`}
                        onClick={() => openPurchaseModal(id, p)}
                        className={`mt-6 w-full ${isFeatured ? 'bg-[#0d63b0] hover:bg-[#0a4f8f] text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
                      >
                        <ShoppingCart className="h-4 w-4 mr-1.5" /> {meta.cta} · €{p.price_eur}/mese
                      </Button>
                    ) : (
                      <Button asChild data-testid={`pricing-cta-${id}`}
                        className={`mt-6 w-full ${isFeatured ? 'bg-[#0d63b0] hover:bg-[#0a4f8f] text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
                      >
                        <Link to="/register">{meta.cta} <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                      </Button>
                    )}
                    <div className="mt-3 text-[11px] text-slate-500 text-center">Fatturato mensilmente · disdici quando vuoi</div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Purchase interest modal — same UX as AccountSubscription */}
      {purchaseModal && (
        <div
          data-testid="purchase-interest-modal"
          className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
          onClick={closePurchaseModal}
        >
          <Card className="max-w-md w-full p-6 border border-slate-200 bg-white relative animate-fade-up" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closePurchaseModal}
              disabled={notifying}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 disabled:opacity-40"
              aria-label="Chiudi"
            >
              <X className="h-4 w-4" />
            </button>

            {!notifySent ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                    <Clock3 className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">Acquisto in fase di sviluppo</div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold">Attivazione manuale</div>
                  </div>
                </div>

                <p className="mt-5 text-sm text-slate-700 leading-relaxed">
                  L'acquisto online è in fase di sviluppo. Se sei interessato al piano <strong className="text-slate-900">{purchaseModal.planInfo.name}</strong> (€{purchaseModal.planInfo.price_eur}/mese), invieremo automaticamente una notifica al team commerciale che ti contatterà per finalizzare.
                </p>

                <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
                  <div className="flex justify-between"><span>Piano</span><span className="font-semibold text-slate-900">{purchaseModal.planInfo.name}</span></div>
                  <div className="flex justify-between"><span>Prezzo</span><span className="font-mono font-semibold text-slate-900">€{purchaseModal.planInfo.price_eur.toFixed(2)}/mese</span></div>
                  <div className="flex justify-between"><span>Ti contatteremo a</span><span className="font-mono text-slate-800 truncate max-w-[180px]">{user?.email || '—'}</span></div>
                </div>

                <p className="mt-4 text-xs text-slate-500 flex items-start gap-1.5">
                  <Mail className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  In alternativa puoi scrivere direttamente a <a href={`mailto:${SALES_EMAIL}`} className="text-[#0d63b0] font-semibold underline">{SALES_EMAIL}</a>
                </p>

                {notifyError && (
                  <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {notifyError}
                  </div>
                )}

                <div className="mt-5 flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={closePurchaseModal} disabled={notifying}>
                    Annulla
                  </Button>
                  <Button
                    onClick={sendPurchaseInterest}
                    disabled={notifying}
                    className="flex-1 bg-[#0d63b0] hover:bg-[#0a4f8f] text-white"
                  >
                    {notifying ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Mail className="h-4 w-4 mr-1.5" /> Invia richiesta</>)}
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-2">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-900">Richiesta inviata</h3>
                <p className="mt-2 text-sm text-slate-600">
                  Abbiamo ricevuto la tua richiesta per il piano <strong>{purchaseModal.planInfo.name}</strong>. Il team commerciale ti contatterà a breve all'indirizzo <span className="font-mono text-slate-900">{user?.email}</span>.
                </p>
                <Button onClick={closePurchaseModal} className="mt-5 w-full bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
                  Ho capito
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}

      <section className="py-14 bg-slate-50">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="bg-blue-100 text-[#0d63b0] border-0">Per studi, aziende e team</Badge>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-slate-900">Il tuo brand, il tuo servizio.</h2>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto">Offri Secure Push ai tuoi clienti con il tuo logo, i tuoi colori e il tuo dominio. Un servizio white-label professionale per il tuo studio o azienda.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {[
              { i: Building2, t: 'White-label', d: 'Nessun riferimento al fornitore. Il tuo brand è l\'unico visibile ai tuoi clienti.' },
              { i: Rocket, t: 'Setup 60 secondi', d: 'Registrati, personalizza logo/colori, condividi il link con il tuo brand.' },
              { i: Crown, t: 'Dominio dedicato', d: 'CNAME + TXT sul tuo dominio: SSL automatico, zero configurazioni.' },
            ].map((f) => (
              <Card key={f.t} className="border border-slate-200 bg-white p-6">
                <f.i className="h-6 w-6 text-[#0d63b0]" />
                <div className="mt-3 font-bold text-slate-900">{f.t}</div>
                <div className="mt-1 text-sm text-slate-600">{f.d}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
